<template>
  <div>
    <PageHeader
      class="mb-3"
      title="Búsqueda de empleado"
      description="Busca por RUT y revisa la información relacionada del empleado."
      :breadcrum-routes="pageHeaderRoutes"
    />

    <a-card class="mb-4">
      <a-form
        ref="formRef"
        layout="vertical"
        :model="formState"
        @submit.prevent="handleSubmit"
      >
        <a-row :gutter="16">
          <a-col :xs="24" :md="12" :lg="10">
            <a-form-item
              label="RUT empleado"
              :validate-status="rutError ? 'error' : ''"
              :help="rutError"
              required
            >
              <a-input
                v-model:value="formState.rut"
                placeholder="Ej: 11.111.111-1"
                @pressEnter="handleSubmit"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <div class="employee-overview-search__actions">
          <a-button type="primary" html-type="submit" :loading="searching">
            Buscar
          </a-button>
          <a-button @click="handleReset" :disabled="searching">
            Limpiar
          </a-button>
        </div>
      </a-form>
    </a-card>

    <a-alert
      v-if="errorMessage"
      class="mb-4"
      type="error"
      show-icon
      :message="errorMessage"
    />

    <a-spin :spinning="searching">
      <template v-if="hasSearched && searchResult">
        <a-card
          title="Datos colaborador"
          class="mb-4"
          bodyStyle="padding: 0px;"
          :bordered="false"
        >
          <a-descriptions bordered>
            <a-descriptions-item label="Nombre completo" :span="3">
              {{ employeeFullName }}
            </a-descriptions-item>
            <a-descriptions-item label="RUT" :span="3">
              {{ formatRut(searchResult.employee?.rut) }}
            </a-descriptions-item>
            <a-descriptions-item label="Correo" :span="3">
              {{ searchResult.employee?.email || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="Fecha de nacimiento" :span="3">
              {{ formatBirthday(searchResult.employee?.birthdayDate) }}
            </a-descriptions-item>
          </a-descriptions>
        </a-card>

        <a-row :gutter="16" class="mb-4">
          <a-col :xs="24" :lg="12" class="mb-3">
            <a-card
              title="Empresas con coberturas vigentes"
              class="employee-overview-search__equal-card"
              :body-style="{ padding: '16px' }"
            >
              <a-empty
                v-if="!companyRows.length"
                description="Sin empresas con coberturas vigentes"
              />
              <a-table
                v-else
                class="employee-overview-search__relation-table"
                :columns="companyColumns"
                :data-source="companyRows"
                :pagination="false"
                :row-key="(record) => record.id || record.rut"
                bordered
              >
                <template #companyRut="{ text }">
                  {{ formatRut(text) }}
                </template>

                <template #companyName="{ record }">
                  {{ getCompanyName(record) }}
                </template>

                <template #companyAction="{ record }">
                  <a-button
                    v-if="getCompanyStockDetailHref(record)"
                    type="primary"
                    :href="getCompanyStockDetailHref(record)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Detalles en stock
                  </a-button>
                  <span v-else>-</span>
                </template>
              </a-table>
            </a-card>
          </a-col>

          <a-col :xs="24" :lg="12" class="mb-3">
            <a-card
              title="Aseguradoras con coberturas vigentes"
              class="employee-overview-search__equal-card"
              :body-style="{ padding: '16px' }"
            >
              <a-empty
                v-if="!activeInsuranceCompanies.length"
                description="Sin aseguradoras con coberturas vigentes"
              />
              <a-table
                v-else
                class="employee-overview-search__relation-table"
                :columns="insuranceCompanyColumns"
                :data-source="activeInsuranceCompanies"
                :pagination="false"
                :row-key="(record) => record.id || record.rut"
                bordered
              >
                <template #insuranceRut="{ text }">
                  {{ formatRut(text) }}
                </template>

                <template #insuranceName="{ record }">
                  {{ getInsuranceCompanyName(record) }}
                </template>
              </a-table>
            </a-card>
          </a-col>
        </a-row>

        <a-card title="Coberturas" class="mb-4">
          <div class="employee-overview-search__coverage-filter">
            <a-radio-group v-model:value="coverageStatusFilter">
              <a-radio-button style="margin: 1px; z-index: 0" value="ACTIVE">
                Vigente
              </a-radio-button>
              <a-radio-button value="INACTIVE">No Vigente</a-radio-button>
            </a-radio-group>
          </div>

          <a-empty
            v-if="!filteredCoverageRows.length"
            :description="coverageEmptyText"
          />
          <a-table
            v-else
            row-key="employeeCoverageId"
            :columns="coverageColumns"
            :data-source="filteredCoverageRows"
            :pagination="false"
            :scroll="{ x: 1100 }"
          >
            <template #coverageInsuranceCompany="{ record }">
              {{ getInsuranceCompanyName(record.insuranceCompany) }}
            </template>

            <template #status="{ text }">
              <a-tag :color="getCoverageStatusColor(text)">
                {{ getCoverageStatusText(text) || '-' }}
              </a-tag>
            </template>

            <template #startDate="{ text }">
              {{ formatDate(text) }}
            </template>

            <template #endDate="{ text }">
              {{ formatDate(text) }}
            </template>
          </a-table>
        </a-card>

        <a-card title="Historial de tickets">
          <a-table
            row-key="id"
            :columns="ticketColumns"
            :data-source="ticketRows"
            :pagination="ticketPagination"
            :scroll="{ x: 900 }"
            @change="handleTicketTableChange"
          >
            <template #status="{ record }">
              <tags
                v-if="record.status?.id"
                :state-id="record.status.id"
                :state="record.status"
              />
              <span v-else>-</span>
            </template>

            <template #createdAt="{ text }">
              {{ formatDate(text) }}
            </template>

            <template #updatedAt="{ text }">
              {{ formatDate(text) }}
            </template>

            <template #insuranceCompany="{ record }">
              {{ getInsuranceCompanyName(record.insuranceCompany) }}
            </template>

            <template #action="{ record }">
              <a-tooltip
                v-if="getTicketDetailHref(record)"
                color="#05045D"
                title="Ver más"
              >
                <a
                  :href="getTicketDetailHref(record)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <eye-outlined />
                </a>
              </a-tooltip>
              <span v-else>-</span>
            </template>
          </a-table>
        </a-card>
      </template>

      <a-card v-else-if="hasSearched && !searchResult && !errorMessage">
        <a-empty
          description="No se encontró información para la búsqueda realizada"
        />
      </a-card>
    </a-spin>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment';
import { EyeOutlined } from '@ant-design/icons-vue';

import { employeeService } from '@/network';
import PageHeader from '@/components/page-header.vue';
import Tags from '@/components/tags.vue';
import getTagColor from '@/utils/getTagColor';
import getTagText from '@/utils/getTagText';
import rutToolBox from '@/utils/Ruts';
import parseDate from '@/utils/dateFormatter';

const route = useRoute();
const router = useRouter();
const MIN_SEARCH_LOADING_MS = 1000;
const pageHeaderRoutes = [
  {
    breadcrumbName: 'Búsqueda de empleado'
  }
];

const formRef = ref();
const searching = ref(false);
const hasSearched = ref(false);
const searchResult = ref(null);
const errorMessage = ref('');
const coverageStatusFilter = ref('ACTIVE');

const formState = reactive({
  rut: route.query.rut ? String(route.query.rut) : ''
});

const ticketRouteMap = {
  1: 'AltasDetalle',
  2: 'BajaDetail',
  3: 'DetalleModificaciones',
  4: 'TransferDetail',
  5: 'AltaCargasDetalle',
  6: 'BajaCargasDetalle'
};

const ticketColumns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    customRender: ({ text }) => `#${text}`
  },
  {
    title: 'Tipo',
    dataIndex: ['type', 'tag'],
    key: 'type'
  },
  {
    title: 'Estado',
    key: 'status',
    slots: { customRender: 'status' }
  },
  {
    title: 'Empresa',
    dataIndex: ['company', 'businessName'],
    key: 'company'
  },
  {
    title: 'Aseguradora',
    key: 'insuranceCompany',
    slots: { customRender: 'insuranceCompany' }
  },
  {
    title: 'Creado',
    dataIndex: 'createdAt',
    key: 'createdAt',
    slots: { customRender: 'createdAt' }
  },
  {
    title: 'Actualizado',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    slots: { customRender: 'updatedAt' }
  },
  {
    title: 'Acción',
    key: 'action',
    slots: { customRender: 'action' }
  }
];

const ticketPagination = reactive({
  current: 1,
  pageSize: 10,
  showSizeChanger: false
});

const coverageColumns = [
  {
    title: 'N° Póliza',
    dataIndex: ['policy', 'numberPolicy'],
    key: 'policyNumber'
  },
  {
    title: 'Aseguradora',
    key: 'insuranceCompany',
    slots: { customRender: 'coverageInsuranceCompany' }
  },
  {
    title: 'Cobertura',
    dataIndex: 'name',
    key: 'name'
  },
  {
    title: 'Estado',
    dataIndex: 'status',
    key: 'status',
    slots: { customRender: 'status' }
  },
  {
    title: 'Inicio vigencia',
    dataIndex: 'startDate',
    key: 'startDate',
    slots: { customRender: 'startDate' }
  },
  {
    title: 'Fin vigencia',
    dataIndex: 'endDate',
    key: 'endDate',
    slots: { customRender: 'endDate' }
  }
];

const companyColumns = [
  {
    title: 'RUT',
    dataIndex: 'rut',
    key: 'rut',
    width: '34%',
    slots: { customRender: 'companyRut' }
  },
  {
    title: 'Razón Social',
    key: 'businessName',
    width: '80%',
    slots: { customRender: 'companyName' }
  },
  {
    title: 'Acciones',
    key: 'action',
    width: '65%',
    slots: { customRender: 'companyAction' }
  }
];

const insuranceCompanyColumns = [
  {
    title: 'RUT',
    dataIndex: 'rut',
    key: 'rut',
    width: '34%',
    slots: { customRender: 'insuranceRut' }
  },
  {
    title: 'Nombre de Aseguradora',
    key: 'businessName',
    width: '66%',
    slots: { customRender: 'insuranceName' }
  }
];

const ticketRows = computed(() => searchResult.value?.tickets ?? []);
const coverageRows = computed(() => searchResult.value?.coverages ?? []);
const activeCoverageRows = computed(() =>
  coverageRows.value.filter(
    (coverage) => String(coverage?.status).toUpperCase() === 'ACTIVE'
  )
);
const inactiveCoverageRows = computed(() =>
  coverageRows.value.filter(
    (coverage) => String(coverage?.status).toUpperCase() !== 'ACTIVE'
  )
);
const filteredCoverageRows = computed(() =>
  coverageStatusFilter.value === 'ACTIVE'
    ? activeCoverageRows.value
    : inactiveCoverageRows.value
);
const coverageEmptyText = computed(() =>
  coverageStatusFilter.value === 'ACTIVE'
    ? 'Sin coberturas vigentes'
    : 'Sin coberturas no vigentes'
);
const companyRows = computed(() => {
  const unique = new Map();

  activeCoverageRows.value
    .map((coverage) => coverage.company)
    .filter(Boolean)
    .forEach((company) => {
      const key = company?.id || company?.rut || company?.businessName;

      if (!key) {
        return;
      }

      unique.set(key, {
        ...(unique.get(key) || {}),
        ...company
      });
    });

  return Array.from(unique.values());
});
const employeeFullName = computed(
  () =>
    searchResult.value?.employee?.fullName ||
    searchResult.value?.employee?.fullname ||
    '-'
);

const activeInsuranceCompanies = computed(() => {
  const insuranceCompanies = activeCoverageRows.value
    .map((coverage) => coverage.insuranceCompany)
    .filter(Boolean);
  const unique = new Map();

  insuranceCompanies.forEach((insurance) => {
    const key = insurance?.id || insurance?.rut || insurance?.businessName;

    if (!key) {
      return;
    }

    const previous = unique.get(key) || {};
    unique.set(key, {
      ...previous,
      ...insurance
    });
  });

  return Array.from(unique.values());
});

const getInsuranceCompanyName = (insurance) => insurance?.businessName || '-';

const getCompanyName = (company) =>
  company?.businessName || company?.name || company?.socialReason || '-';

const rutError = computed(() => {
  if (!formState.rut) return 'Ingrese RUT';
  if (!rutToolBox.validateRut(formState.rut)) {
    return 'Ingrese un RUT válido';
  }
  return '';
});

const formatRut = (rut) => rutToolBox.formatRut(rut) || '-';

const formatBirthday = (date) => {
  if (!date) return '-';
  return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
};

const formatDate = (date) => {
  if (!date) return '-';
  return parseDate(date);
};

const getCoverageStatusColor = (status) => getTagColor(status);
const getCoverageStatusText = (status) => getTagText(status);

const handleTicketTableChange = (pagination) => {
  ticketPagination.current = pagination.current;
};

const ticketRouteName = (ticket) => ticketRouteMap[ticket?.type?.id];

const getTicketDetailHref = (ticket) => {
  const name = ticketRouteName(ticket);
  if (!name) return '';

  return router.resolve({
    name,
    params: { id: ticket.id }
  }).href;
};

const getStockDetailHref = (company) => {
  const companyId = company?.id || company?.stockDetail?.companyId;
  const employeeId =
    company?.stockDetail?.employeeId || searchResult.value?.employee?.id;

  if (companyId && employeeId) {
    return router.resolve({
      name: 'Colaborador',
      params: {
        id: companyId,
        colaborador: employeeId
      }
    }).href;
  }

  return company?.stockDetail?.frontPath || '';
};

const getCompanyStockDetailHref = (company) => getStockDetailHref(company);

const clearSearchState = () => {
  hasSearched.value = false;
  searchResult.value = null;
  errorMessage.value = '';
  coverageStatusFilter.value = 'ACTIVE';
  ticketPagination.current = 1;
};

const search = async (rut) => {
  searching.value = true;
  errorMessage.value = '';
  coverageStatusFilter.value = 'ACTIVE';
  ticketPagination.current = 1;

  try {
    const [response] = await Promise.all([
      employeeService.getEmployeeOverview({
        rut
      }),
      new Promise((resolve) => setTimeout(resolve, MIN_SEARCH_LOADING_MS))
    ]);

    searchResult.value = response;
  } catch (error) {
    searchResult.value = null;

    if (error?.response?.status === 404) {
      errorMessage.value = 'No se encontró empleado con ese RUT';
      return;
    }

    errorMessage.value =
      error?.response?.data?.message ||
      'Ocurrió un error al buscar el empleado';
  } finally {
    hasSearched.value = true;
    searching.value = false;
  }
};

const handleSubmit = async () => {
  if (rutError.value) {
    clearSearchState();
    return;
  }

  await router.replace({
    name: route.name,
    query: {
      ...route.query,
      rut: rutToolBox.normalizeRut(formState.rut)
    }
  });
};

const handleReset = async () => {
  formRef.value?.resetFields();
  const query = { ...route.query };
  delete query.rut;
  clearSearchState();
  await router.replace({ name: route.name, query });
};

watch(
  () => route.query.rut,
  async (rut) => {
    formState.rut = rut ? String(rut) : '';

    if (!rut) {
      clearSearchState();
      return;
    }

    await search(String(rut));
  },
  { immediate: true }
);
</script>

<style scoped>
.employee-overview-search__actions {
  display: flex;
  gap: 12px;
}

.employee-overview-search__coverage-filter {
  display: flex;
  margin-bottom: 16px;
}

.employee-overview-search__equal-card {
  height: 100%;
}

.employee-overview-search__equal-card :deep(.ant-card-body) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.employee-overview-search__relation-table {
  flex: 1;
}

.employee-overview-search__relation-table :deep(.ant-table) {
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
}

.employee-overview-search__relation-table :deep(.ant-table-container) {
  border-radius: 8px;
}

.employee-overview-search__relation-table :deep(table) {
  width: 100% !important;
  table-layout: fixed;
}

.employee-overview-search__relation-table :deep(.ant-table-thead > tr > th) {
  background: #f4f6f8;
  color: #1f1f1f;
  font-weight: 500;
}

.employee-overview-search__relation-table :deep(.ant-table-tbody > tr > td) {
  height: 64px;
  vertical-align: middle;
  background: #fff;
}

.employee-overview-search__relation-table
  :deep(.ant-table-tbody > tr:hover > td) {
  background: #fff !important;
}

.employee-overview-search__relation-table :deep(.ant-table-pagination) {
  display: none;
}

@media (max-width: 768px) {
  .employee-overview-search__actions {
    flex-direction: column;
  }
  .employee-overview-search__equal-card {
    height: 100%;
  }

  .employee-overview-search__equal-card :deep(.ant-card-body) {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .employee-overview-search__relation-table {
    flex: 1;
  }

  .employee-overview-search__relation-table :deep(.ant-table-tbody > tr > td) {
    height: 64px;
  }
}
</style>
